import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {Collapse, ListItemButton, ListItemText, List, ClickAwayListener} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {Can} from "../../context/AuthzProvider";
import {useSidebar} from "./Context/SidebarProvider";
import {useFeatureFlags} from "../../context/FeatureFlagProvider";
import UpgradeAdornment from "../UpgradeAdornment";
import SidebarIcon from "./SidebarIcon";
import StyledTooltip from "../tooltips/StyledTooltip";
import {SidebarOverlay} from "./SidebarOverlay";
import {useSidebarItemStyles} from "./sidebarStyles";
import {includes} from "underscore";

const SidebarMenu = ({children, hide, icon, needUpgrade, relatedModelName, text}) => {
  const {newSidebarDesign} = useFeatureFlags();
  const {isSidebarHidden, sidebarMenuOpen, toggleSidebarMenu, currentPage, showSidebarText} =
    useSidebar();
  const [isOverlayShown, setIsOverlayShown] = useState(false);
  const [overlayLeft, setOverlayLeft] = useState(0);
  const [overlayTop, setOverlayTop] = useState(0);
  const [buttonHeight, setButtonHeight] = useState(0);
  const buttonRef = React.useRef(null);
  const location = useLocation();

  useEffect(() => {
    setIsOverlayShown(false);
  }, [location.pathname]);

  if (hide) return null;

  const anchor = overlayTop > window.innerHeight / 2 ? "up" : "down";

  const isCurrentParent =
    newSidebarDesign &&
    React.Children.toArray(children).some((child) => {
      if (React.isValidElement(child) && child.props.to) {
        return child.props.to.includes(currentPage);
      }
      return false;
    });

  const shouldShowText = newSidebarDesign ? showSidebarText : !isSidebarHidden;
  const {classes} = useSidebarItemStyles({
    newSidebarDesign,
    isSidebarHidden: newSidebarDesign ? !showSidebarText : isSidebarHidden,
    isCurrentParent,
    inOverlay: false,
  });

  const isOpen = includes(sidebarMenuOpen, text);

  const handleClick = () => {
    if (newSidebarDesign) {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setOverlayLeft(rect.right);
        setOverlayTop(rect.top);
        setButtonHeight(rect.height);
      }
      setIsOverlayShown(!isOverlayShown);
    } else {
      if (!needUpgrade) toggleSidebarMenu(text);
    }
  };

  const handleKeyDown = (e) => {
    if (newSidebarDesign && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setIsOverlayShown((prev) => !prev);
    }
  };

  const childrenWithProps = newSidebarDesign ?
    React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {inOverlay: true});
      }
      return child;
    }) :
    children;

  const menuButton = (
    <ListItemButton
      ref={buttonRef}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      className={classes.item}
      id={`sidebar-menu-${text}`}
      tabIndex={0}
      aria-haspopup="true"
      aria-expanded={isOverlayShown}
    >
      <SidebarIcon icon={icon} text={text} />
      <ListItemText
        primary={text}
        className={shouldShowText ? classes.overlayItemText : classes.itemText}
        primaryTypographyProps={{variant: "body2"}}
      />
      {needUpgrade && <UpgradeAdornment fontSize="small" item={text} />}
      {newSidebarDesign && !needUpgrade && showSidebarText && (
        <ChevronRightIcon
          fontSize="small"
          sx={{
            position: "absolute",
            right: 4,
            transform: isOverlayShown ? "rotate(0deg)" : "rotate(90deg)",
            transition: "transform 0.2s",
          }}
        />
      )}
    </ListItemButton>
  );

  if (newSidebarDesign) {
    return (
      <Can I="update" a={relatedModelName}>
        <ClickAwayListener onClickAway={() => setIsOverlayShown(false)}>
          <div>
            {showSidebarText ? (
              menuButton
            ) : (
              <StyledTooltip title={text} placement="right" opacity={isOverlayShown ? 0 : 1}>
                {menuButton}
              </StyledTooltip>
            )}
            {!needUpgrade && (
              <SidebarOverlay
                open={isOverlayShown}
                sidebarWidth={overlayLeft}
                top={overlayTop}
                anchor={anchor}
                buttonHeight={buttonHeight}
                parentText={!showSidebarText ? text : null}
              >
                <List component="div" disablePadding>
                  {childrenWithProps}
                </List>
              </SidebarOverlay>
            )}
          </div>
        </ClickAwayListener>
      </Can>
    );
  }

  return (
    <Can I="update" a={relatedModelName}>
      <ListItemButton onClick={handleClick} className={classes.item} id={`sidebar-menu-${text}`}>
        <SidebarIcon icon={icon} text={text} />
        <ListItemText
          primary={text}
          className={classes.itemText}
          primaryTypographyProps={{variant: "body2"}}
        />
        {needUpgrade ? (
          <UpgradeAdornment fontSize="small" item={text} />
        ) : isOpen ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.menuList}>
          {children}
        </List>
      </Collapse>
    </Can>
  );
};

SidebarMenu.propTypes = {
  children: PropTypes.any.isRequired,
  hide: PropTypes.bool,
  icon: PropTypes.element.isRequired,
  needUpgrade: PropTypes.bool,
  relatedModelName: PropTypes.string,
  text: PropTypes.string.isRequired,
};

export default SidebarMenu;
